import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as dataApi from '../services/dataApi';
import { useTheme } from './useTheme';
import {
  normalizeCard,
  normalizeDeck,
  normalizeSettings,
  normalizeReviewHistory,
  normalizeGrammarProgress,
} from './normalizers';
import { reportError } from '@/services/errorTracking';
import { createCardsSlice, type CardsSlice } from './slices/cardsSlice';
import { createDecksSlice, type DecksSlice } from './slices/decksSlice';
import { createSettingsSlice, type SettingsSlice } from './slices/settingsSlice';
import { createReviewSlice, type ReviewSlice } from './slices/reviewSlice';
import {
  createGrammarProgressSlice,
  type GrammarProgressSlice,
} from './slices/grammarProgressSlice';
import { createTutorSlice, type TutorSlice } from './slices/tutorSlice';
import { defaultDecks, defaultSettings } from './defaults';

// Re-export so existing consumers (if any) still work
export { defaultDecks, defaultSettings };

// ─── Core Slice (orchestration + cross-cutting concerns) ─────

interface CoreSlice {
  currentDeckId: string;
  isLoading: boolean;
  dataLoaded: boolean;
  setDataLoaded: (loaded: boolean) => void;
  loadFromApi: () => Promise<void>;
  refreshFromApi: () => Promise<void>;
  setCurrentDeckId: (deckId: string) => void;
  resetStore: () => void;
}

// ─── Combined Store Type ─────────────────────────────────────

export type StoreState = CardsSlice &
  DecksSlice &
  SettingsSlice &
  ReviewSlice &
  GrammarProgressSlice &
  TutorSlice &
  CoreSlice;

// ─── Store ───────────────────────────────────────────────────

export const useStore = create<StoreState>()(
  persist(
    (...a) => {
      const [set, get] = a;
      return {
        // Compose domain slices
        ...createCardsSlice(...a),
        ...createDecksSlice(...a),
        ...createSettingsSlice(...a),
        ...createReviewSlice(...a),
        ...createGrammarProgressSlice(...a),
        ...createTutorSlice(...a),

        // Core state
        currentDeckId: 'deck-general',
        isLoading: false,
        dataLoaded: false,
        setDataLoaded: (dataLoaded) => set({ dataLoaded }),

        // Orchestrated data loading (touches all slices)
        loadFromApi: async () => {
          if (get().dataLoaded || get().isLoading) return;
          set({ isLoading: true });
          try {
            const [cardsRaw, decksRaw, settingsRaw, reviewHistoryRaw, grammarProgressRaw] =
              await Promise.all([
                dataApi.fetchCards(),
                dataApi.fetchDecks(),
                dataApi.fetchUserSettings(),
                dataApi.fetchReviewHistory(30),
                dataApi.fetchGrammarProgress().catch(() => []),
              ]);
            const cards = (Array.isArray(cardsRaw) ? cardsRaw : []).map((r) =>
              normalizeCard(r as Record<string, unknown>),
            );
            let decks = (Array.isArray(decksRaw) ? decksRaw : []).map((r) =>
              normalizeDeck(r as Record<string, unknown>),
            );

            if (decks.length === 0) {
              try {
                for (const d of defaultDecks) {
                  const res = (await dataApi.createDeck({
                    name: d.name,
                    emoji: d.emoji,
                    languagePair: d.languagePair,
                    isDefault: d.isDefault,
                  })) as Record<string, unknown>;
                  decks.push(normalizeDeck(res));
                }
              } catch (deckErr) {
                reportError(deckErr, { operation: 'createDefaultDecks' });
                const freshDecks = await dataApi.fetchDecks();
                decks = (Array.isArray(freshDecks) ? freshDecks : []).map((r) =>
                  normalizeDeck(r as Record<string, unknown>),
                );
              }
            }

            const reviewHistory = (Array.isArray(reviewHistoryRaw) ? reviewHistoryRaw : []).map(
              (r) => normalizeReviewHistory(r as Record<string, unknown>),
            );
            const grammarProgress = (
              Array.isArray(grammarProgressRaw) ? grammarProgressRaw : []
            ).map((r) => normalizeGrammarProgress(r as Record<string, unknown>));
            const settings = normalizeSettings((settingsRaw ?? {}) as Record<string, unknown>);

            // Sync theme from Supabase for cross-device consistency
            if (['light', 'dark', 'system'].includes(settings.theme)) {
              useTheme.getState().setTheme(settings.theme);
            }

            const defaultDeck = decks.find((d) => d.isDefault) ?? decks[0];
            set({
              cards,
              decks,
              settings,
              reviewHistory,
              grammarProgress,
              currentDeckId: defaultDeck?.id ?? decks[0]?.id ?? 'deck-general',
              dataLoaded: true,
            });
          } catch (err) {
            reportError(err, { operation: 'loadFromApi' });
            set({ dataLoaded: false });
            throw err;
          } finally {
            set({ isLoading: false });
          }
        },

        // Force re-fetch all data (used by pull-to-refresh)
        refreshFromApi: async () => {
          // Atomically reset both flags so loadFromApi's guard allows re-entry
          set({ dataLoaded: false, isLoading: false });
          await get().loadFromApi();
        },

        setCurrentDeckId: (deckId) => {
          set({ currentDeckId: deckId });
        },

        resetStore: () => {
          set({
            cards: [],
            decks: defaultDecks,
            settings: defaultSettings,
            reviewHistory: [],
            grammarProgress: [],
            tutorConcepts: [],
            curriculum: [],
            currentSession: null,
            messages: [],
            currentDeckId: 'deck-general',
            isLoading: false,
            dataLoaded: false,
          });
        },
      };
    },
    {
      name: 'vidi-store',
      version: 2,
      partialize: (state) => ({ currentDeckId: state.currentDeckId }),
    },
  ),
);
